/**
 * Event Types page (/events/event-types) — list + Add/Edit form (view toggle).
 *
 * Full list: topic badges, Entity · Action, publisher/subscriber, pagination, search and
 * 4 filters (status, entity, action, publisher) + "Clear filters". Add/Edit open the
 * EventTypeForm; archive is interactive against the mocks via local state.
 *
 * NOTE: viewer-only button hiding can't gate on a role yet (v1.0.36 removed roles). `canManage`
 * is a true placeholder (TODO: RBAC).
 */

import * as React from 'react'
import { useApiQuery, useApiMutation } from '@/api/ApiQueryServiceProvider'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog'
import { Badge } from '@/components/ui/badge'
import { useToast } from '@/contexts/ToastContext'
import { AppLogger } from '@/lib/logger'
import { Search, Edit, Trash2, Plus, Loader2, Webhook } from 'lucide-react'
import type { EventType } from '@/types/events'
import { EVENT_WORKLOAD_INSTANCES } from '@/forms/events/eventConstants'
import { EventTypeForm } from './EventTypeForm'

const log = AppLogger.getLogger(import.meta)
const cap = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s)
const ITEMS_PER_PAGE = 10
type View = 'list' | 'create' | 'edit'

export function EventTypeList() {
  const { toast } = useToast()
  const { data, isLoading, isError, refetch } = useApiQuery('getEventTypes', {}, {})
  const { data: instancesData } = useApiQuery('getResourceInstances', {}, {})

  const apiInstances = ((instancesData as any)?.content?.resourceInstances ?? []) as any[]
  const sourceInstances = apiInstances.length > 0 ? apiInstances : EVENT_WORKLOAD_INSTANCES
  const instanceName = (uri: string) => {
    const match = sourceInstances.find((i) => i.instanceUri === uri)
    return match?.resourceInstanceName ?? uri
  }

  const list = (((data as any)?.content?.eventTypes ?? []) as EventType[])

  const archiveMut = useApiMutation('archiveEventType', 'DELETE', { invalidates: ['getEventTypes'] })

  const [view, setView] = React.useState<View>('list')
  const [selected, setSelected] = React.useState<EventType | null>(null)
  const [archiving, setArchiving] = React.useState<EventType | null>(null)

  const [search, setSearch] = React.useState('')
  const [statusFilter, setStatusFilter] = React.useState('all')
  const [entityFilter, setEntityFilter] = React.useState('all')
  const [actionFilter, setActionFilter] = React.useState('all')
  const [publisherFilter, setPublisherFilter] = React.useState('all')
  const [page, setPage] = React.useState(1)
  React.useEffect(() => { setPage(1) }, [search, statusFilter, entityFilter, actionFilter, publisherFilter])

  const canManage = true // TODO: gate on RBAC

  const entitiesPresent = Array.from(new Set(list.map((e) => e.entityTypeName).filter(Boolean))) as string[]
  const actionsPresent = Array.from(new Set(list.map((e) => e.actionName).filter(Boolean))) as string[]
  const publishersPresent = Array.from(new Set(list.map((e) => e.publisherUri).filter(Boolean)))

  const anyFilter = !!search || statusFilter !== 'all' || entityFilter !== 'all' || actionFilter !== 'all' || publisherFilter !== 'all'
  const clearFilters = () => { setSearch(''); setStatusFilter('all'); setEntityFilter('all'); setActionFilter('all'); setPublisherFilter('all') }

  const filtered = list.filter((e) => {
    const q = search.trim().toLowerCase()
    const matchesSearch = !q || [e.eventTypeLabel, e.eventTypeName, e.eventTypeDescription, e.entityTypeName, e.actionName].some((f) => (f ?? '').toLowerCase().includes(q))
    const matchesStatus = statusFilter === 'all' || e.status === statusFilter
    const matchesEntity = entityFilter === 'all' || e.entityTypeName === entityFilter
    const matchesAction = actionFilter === 'all' || e.actionName === actionFilter
    const matchesPublisher = publisherFilter === 'all' || e.publisherUri === publisherFilter
    return matchesSearch && matchesStatus && matchesEntity && matchesAction && matchesPublisher
  }).sort((a, b) => a.eventTypeLabel.localeCompare(b.eventTypeLabel))

  const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE) || 1
  const paginated = filtered.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE)

  // The upsert mutation invalidates getEventTypes, so the list refetches from the backend.
  const afterFormSuccess = () => { setView('list'); setSelected(null) }

  const handleArchive = async () => {
    if (!archiving) return
    try {
      await archiveMut.mutateAsync({ eventTypeNumber: archiving.eventTypeNumber })
      setArchiving(null)
      toast({ title: 'Event type archived', type: 'success' })
    } catch (e) { log.error('archive failed', e); toast({ title: 'Could not archive', description: (e as Error)?.message, type: 'error' }); setArchiving(null) }
  }

  if (view === 'create') return <EventTypeForm mode="add" onSuccess={afterFormSuccess} onCancel={() => setView('list')} />
  if (view === 'edit' && selected) return <EventTypeForm mode="edit" initial={selected} onSuccess={afterFormSuccess} onCancel={() => { setView('list'); setSelected(null) }} />

  if (isLoading) return <Card className="shadow-sm"><CardContent className="flex items-center justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></CardContent></Card>
  if (isError) return <Card className="shadow-sm"><CardContent className="flex flex-col items-center justify-center py-12 gap-4"><p className="text-destructive">Failed to load event types</p><Button variant="outline" onClick={() => refetch()}>Try Again</Button></CardContent></Card>

  return (
    <div className="space-y-4">
      <Card className="shadow-sm pb-0">
        <CardHeader className="border-b bg-slate-700 text-white [&_p]:text-slate-300">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10"><Webhook className="h-5 w-5 text-primary" /></div>
              <div><CardTitle className="text-xl">Event Types</CardTitle><CardDescription>The events the platform publishes</CardDescription></div>
            </div>
            {canManage && <Button size="sm" className="gap-2" onClick={() => setView('create')}><Plus className="h-4 w-4" />Add Event Type</Button>}
          </div>
        </CardHeader>
      </Card>

      <Card className="shadow-sm pb-0">
        <CardContent className="p-4">
          <div className="flex flex-col gap-3">
            <div className="relative max-w-sm">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input placeholder="Search event types..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" />
            </div>
            <div className="flex flex-wrap gap-2">
              <Select value={statusFilter} onValueChange={setStatusFilter}><SelectTrigger className="w-[150px]"><SelectValue placeholder="Status" /></SelectTrigger><SelectContent><SelectItem value="all">All statuses</SelectItem><SelectItem value="active">Active</SelectItem><SelectItem value="inactive">Inactive</SelectItem></SelectContent></Select>
              <Select value={entityFilter} onValueChange={setEntityFilter}><SelectTrigger className="w-[150px]"><SelectValue placeholder="Entity" /></SelectTrigger><SelectContent><SelectItem value="all">All entities</SelectItem>{entitiesPresent.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}</SelectContent></Select>
              <Select value={actionFilter} onValueChange={setActionFilter}><SelectTrigger className="w-[150px]"><SelectValue placeholder="Action" /></SelectTrigger><SelectContent><SelectItem value="all">All actions</SelectItem>{actionsPresent.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent></Select>
              <Select value={publisherFilter} onValueChange={setPublisherFilter}><SelectTrigger className="w-[180px]"><SelectValue placeholder="Publisher" /></SelectTrigger><SelectContent><SelectItem value="all">All publishers</SelectItem>{publishersPresent.map((p) => <SelectItem key={p} value={p}>{instanceName(p)}</SelectItem>)}</SelectContent></Select>
              {anyFilter && <Button variant="ghost" size="sm" onClick={clearFilters}>Clear filters</Button>}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="shadow-sm pb-0">
        <CardContent className="p-4">
          <Table className="text-base">
            <TableHeader>
              <TableRow className="bg-muted/30 hover:bg-muted/30">
                <TableHead className="font-semibold">Label</TableHead>
                <TableHead className="font-semibold">Name</TableHead>
                <TableHead className="font-semibold">Entity · Action</TableHead>
                <TableHead className="font-semibold">Topic</TableHead>
                <TableHead className="font-semibold">Publisher</TableHead>
                <TableHead className="font-semibold text-center">Subs</TableHead>
                <TableHead className="font-semibold text-center">Status</TableHead>
                <TableHead className="w-[120px] text-right font-semibold">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {paginated.length > 0 ? paginated.map((e) => {
                const archived = !!e.archiveDate
                return (
                  <TableRow key={e.eventTypeNumber} className="hover:bg-muted/50">
                    <TableCell className="font-medium">{e.eventTypeLabel}</TableCell>
                    <TableCell className="font-mono text-sm text-muted-foreground">{e.eventTypeName}</TableCell>
                    <TableCell>{e.entityTypeName} · {e.actionName}</TableCell>
                    <TableCell>{e.topic ? <Badge variant="outline">{e.topic}</Badge> : <span className="text-muted-foreground">—</span>}</TableCell>
                    <TableCell className="text-sm" title={e.publisherUri}>{instanceName(e.publisherUri) === e.publisherUri ? <span className="text-amber-600">Not found in ReMa</span> : instanceName(e.publisherUri)}</TableCell>
                    <TableCell className="text-center"><Badge variant="secondary">{e.subscriberUris.length}</Badge></TableCell>
                    <TableCell className="text-center"><Badge variant={archived ? 'secondary' : e.status === 'active' ? 'default' : 'secondary'}>{archived ? 'Archived' : cap(e.status)}</Badge></TableCell>
                    <TableCell className="text-right"><div className="flex items-center justify-end gap-1">
                      {canManage && !archived && (<>
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { setSelected(e); setView('edit') }}><Edit className="h-4 w-4" /></Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:bg-destructive/10 hover:text-destructive" onClick={() => setArchiving(e)}><Trash2 className="h-4 w-4" /></Button>
                      </>)}
                    </div></TableCell>
                  </TableRow>
                )
              }) : (
                <TableRow><TableCell colSpan={8} className="h-24 text-center text-muted-foreground">
                  {anyFilter ? (<>No event types match your filters. <button className="text-primary underline" onClick={clearFilters}>Clear filters</button></>) : 'No event types yet.'}
                </TableCell></TableRow>
              )}
            </TableBody>
          </Table>
          {totalPages > 1 && (
            <div className="flex items-center justify-between border-t py-3">
              <p className="text-sm text-muted-foreground">Page {page} of {totalPages}</p>
              <div className="flex gap-2"><Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage((p) => p - 1)}>Previous</Button><Button variant="outline" size="sm" disabled={page === totalPages} onClick={() => setPage((p) => p + 1)}>Next</Button></div>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!archiving} onOpenChange={(o) => !o && setArchiving(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Archive "{archiving?.eventTypeLabel}"?</DialogTitle>
            <DialogDescription>Publishers will stop sending this event once they reload. The name and type URI can't be reused.</DialogDescription></DialogHeader>
          <DialogFooter><Button variant="outline" onClick={() => setArchiving(null)}>Cancel</Button><Button variant="destructive" onClick={handleArchive} disabled={archiveMut.isPending}>Archive</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
